import { awsUserService } from './aws-user-service';

/**
 * Service to track API and Database usage
 * Buffers usage metrics and periodically flushes them to the user profile
 */
class UsageTracker {
    // Buffer map: userId -> date -> { reads, writes }
    private buffer: Record<string, Record<string, { reads: number; writes: number; storageBytes: number }>> = {};
    private flushInterval: NodeJS.Timeout | null = null;
    private FLUSH_DELAY_MS = 60 * 1000; // 1 minute
    private currentUserId: string | null = null;
    private currentUserEmail: string | null = null;
    private isAdmin: boolean = false;

    constructor() {
        this.startAutoFlush();
    }

    public setUser(userId: string, email: string, isAdmin: boolean = false) {
        this.currentUserId = userId;
        this.currentUserEmail = email;
        this.isAdmin = isAdmin;
    }

    /**
     * Get the effective user ID for tracking.
     * If Admin is logged in, we track against 'ADMIN_PANEL' virtual user
     * or the admin's own user ID, but user requested "Add admin panel usage also".
     * Separating it makes it clearer.
     */
    private getTrackingId(): string {
        if (this.isAdmin) return 'ADMIN_PANEL';
        return this.currentUserId || 'anonymous';
    }

    /**
     * Track usage from API calls or DB operations
     * @param type 'read' | 'write'
     * @param units Number of units
     */
    public track(type: 'read' | 'write', units: number = 1) {
        if (!this.currentUserId) return; // Don't track if not logged in

        const trackingId = this.getTrackingId();
        const dateKey = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

        if (!this.buffer[trackingId]) {
            this.buffer[trackingId] = {};
        }
        if (!this.buffer[trackingId][dateKey]) {
            this.buffer[trackingId][dateKey] = { reads: 0, writes: 0, storageBytes: 0 };
        }

        if (type === 'read') {
            this.buffer[trackingId][dateKey].reads += units;
        } else {
            this.buffer[trackingId][dateKey].writes += units;
        }
    }

    private startAutoFlush() {
        if (this.flushInterval) return;
        this.flushInterval = setInterval(() => {
            this.flush();
        }, this.FLUSH_DELAY_MS);
    }

    public async flush() {
        if (Object.keys(this.buffer).length === 0) return;

        const bufferSnapshot = JSON.parse(JSON.stringify(this.buffer));
        // Reset buffer
        this.buffer = {};

        for (const userId of Object.keys(bufferSnapshot)) {
            const days = bufferSnapshot[userId];
            for (const date of Object.keys(days)) {
                const stats = days[date];
                if (stats.reads === 0 && stats.writes === 0) continue;

                try {
                    console.log(`[UsageTracker] Flushing for ${userId} [${date}]: ${stats.reads}R, ${stats.writes}W`);
                    await awsUserService.updateDailyUsage(userId, date, stats.reads, stats.writes);
                } catch (error) {
                    console.error(`[UsageTracker] Failed usage flush for ${userId}:`, error);
                    // Re-queue could be implemented here, skipping for simplicity in this demo
                }
            }
        }
    }
}

export const usageTracker = new UsageTracker();
